import React, { useState } from "react";
import { DateTime } from "luxon";
import {
  Modal,
  Box,
  Typography,
  Button,
  Grid2 as Grid,
  Stack,
  Link,
  Card,
  Divider,
  CardActions,
  CardContent,
  IconButton,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tooltip,
} from "@mui/material";
import {
  Close as CloseIcon,
  Schedule,
  // Cancel,
  Email,
  Launch,
  Person,
  LocationOn,
  AccessTime,
} from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { DateTimePicker } from "@mui/x-date-pickers";
import axios from "axios";

function UpcomingNotarizationModal({
  open,
  onClose,
  selectedJob,
  currentUser,
  currentSession,
}) {
  const [action, setAction] = useState("");
  const [rescheduleDialogOpen, setRescheduleDialogOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [copiedDocKey, setCopiedDocKey] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Reschedule form data
  const [newDateTime, setNewDateTime] = useState(null);
  const [rescheduleReason, setRescheduleReason] = useState("");

  const formatDateTime = (dateString) => {
    if (!dateString) return "N/A";
    const utcDate = DateTime.fromISO(dateString, { zone: "utc" });
    const localDate = utcDate.setZone(currentUser?.timezone);
    return localDate.toFormat("MMMM dd, yyyy hh:mm a");
  };

  const getCurrNotarization = () =>
    selectedJob?.notarization?.notarization?.bn_session;

  const getStatusInfo = () => {
    if (!selectedJob?.booked_date)
      return { status: "Scheduled", color: "primary", icon: <Schedule /> };

    const bookedDateUtc = DateTime.fromISO(selectedJob.booked_date, {
      zone: "utc",
    });
    const nowUTC = DateTime.utc();
    const timeDifference = bookedDateUtc - nowUTC;

    if (timeDifference <= 300000) {
      // 5 minutes
      return { status: "Ready to Start", color: "success", icon: <Launch /> };
    } else if (timeDifference <= 3600000) {
      // 1 hour
      return {
        status: "Starting Soon",
        color: "warning",
        icon: <AccessTime />,
      };
    }
    return { status: "Scheduled", color: "primary", icon: <Schedule /> };
  };

  const handleCopyToClipboard = async (value, docKey) => {
    if (!value) return;

    try {
      await navigator.clipboard.writeText(value);
      setCopiedDocKey(docKey);
      setTimeout(() => setCopiedDocKey(null), 900);
    } catch (error) {
      console.error("Failed to copy text:", error);
    }
  };

  const handleSendReminder = async () => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/notary/send-reminder-email`,
        {
          jobId: selectedJob.id,
          selectedJob: selectedJob,
          currentSession,
        },
        { withCredentials: true },
      );

      if (response.data.success) {
        setSuccess("Reminder email sent successfully!");
      }
    } catch (error) {
      setError(
        error.response?.data?.message || "Failed to send reminder email",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleReschedule = async () => {
    if (!newDateTime || !rescheduleReason.trim()) {
      setError("Please select a new date/time and provide a reason");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/notary/reschedule-appointment`,
        {
          jobId: selectedJob.id,
          newDateTime: newDateTime.toISOString(),
          reason: rescheduleReason,
          currentSession,
        },
        { withCredentials: true },
      );

      if (response.data.success) {
        setSuccess(
          "Appointment rescheduled successfully! Customer has been notified.",
        );
        setRescheduleDialogOpen(false);
        onClose();
      }
    } catch (error) {
      setError(
        error.response?.data?.message || "Failed to reschedule appointment",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/notary/cancel-appointment`,
        {
          jobId: selectedJob.id,
          currentSession,
        },
        { withCredentials: true },
      );

      if (response.data.success) {
        setSuccess(
          "Appointment cancelled successfully! The slot will be reassigned.",
        );
        setCancelDialogOpen(false);
        onClose();
      }
    } catch (error) {
      setError(error.response?.data?.message || "Failed to cancel appointment");
    } finally {
      setLoading(false);
    }
  };

  const statusInfo = getStatusInfo();

  if (!selectedJob) return null;

  return (
    <>
      <Modal open={open} onClose={onClose}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "95%", md: "80%", lg: "70%" },
            maxWidth: "1000px",
            bgcolor: "background.paper",
            boxShadow: 24,
            borderRadius: 2,
            maxHeight: "90vh",
            overflow: "auto",
          }}
        >
          <IconButton
            onClick={onClose}
            sx={{ position: "absolute", right: 10, top: 10, zIndex: 1 }}
          >
            <CloseIcon />
          </IconButton>

          <Card>
            <CardContent sx={{ p: 4 }}>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                mb={3}
              >
                <Typography variant="h4" fontWeight="bold">
                  Upcoming Notarization Details
                </Typography>
                <Chip
                  icon={statusInfo.icon}
                  label={statusInfo.status}
                  color={statusInfo.color}
                  size="large"
                  sx={{ px: 2, py: 1, fontSize: "1rem" }}
                />
              </Box>

              {error && (
                <Alert
                  severity="error"
                  sx={{ mb: 2 }}
                  onClose={() => setError("")}
                >
                  {error}
                </Alert>
              )}

              {success && (
                <Alert
                  severity="success"
                  sx={{ mb: 2 }}
                  onClose={() => setSuccess("")}
                >
                  {success}
                </Alert>
              )}

              <Grid container spacing={3}>
                {/* Appointment Information */}
                <Grid size={{ xs: 12, md: 4 }}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" color="primary" gutterBottom>
                        <AccessTime sx={{ mr: 1, verticalAlign: "middle" }} />
                        Appointment Details
                      </Typography>
                      <Divider sx={{ mb: 2 }} />
                      <Stack spacing={2}>
                        <Box>
                          <Typography variant="subtitle2" color="textSecondary">
                            Scheduled Time
                          </Typography>
                          <Typography variant="body1" fontWeight={500}>
                            {formatDateTime(selectedJob.booked_date)}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="subtitle2" color="textSecondary">
                            Document Type
                          </Typography>
                          <Typography variant="body1">
                            {selectedJob.document?.type || "Permit Package"}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="subtitle2" color="textSecondary">
                            Notarization Type
                          </Typography>
                          <Chip
                            label={
                              selectedJob.type === "immediate"
                                ? "Expedited"
                                : "Scheduled"
                            }
                            color={
                              selectedJob.type === "immediate"
                                ? "error"
                                : "primary"
                            }
                            size="small"
                          />
                        </Box>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Signer Information */}
                <Grid size={{ xs: 12, md: 4 }}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" color="primary" gutterBottom>
                        <Person sx={{ mr: 1, verticalAlign: "middle" }} />
                        Signer Information
                      </Typography>
                      <Divider sx={{ mb: 2 }} />
                      <Stack spacing={2}>
                        <Box>
                          <Typography variant="subtitle2" color="textSecondary">
                            Full Name
                          </Typography>
                          <Typography variant="body1" fontWeight={500}>
                            {selectedJob?.signer_details?.first +
                              " " +
                              selectedJob?.signer_details?.last || "N/A"}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="subtitle2" color="textSecondary">
                            Email Address
                          </Typography>
                          <Link
                            href={`mailto:${selectedJob?.signer_details?.email}`}
                            color="inherit"
                          >
                            <Typography variant="body2">
                              {selectedJob?.signer_details?.email || "N/A"}
                            </Typography>
                          </Link>
                        </Box>
                        <Box>
                          <Typography variant="subtitle2" color="textSecondary">
                            Phone Number
                          </Typography>
                          <Typography variant="body2">
                            {selectedJob?.signer_details?.mobile || "N/A"}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="subtitle2" color="textSecondary">
                            Address
                          </Typography>
                          <Typography variant="body2">
                            {selectedJob?.signer_details?.address +
                              ", " +
                              selectedJob?.signer_details?.city +
                              " " +
                              selectedJob?.signer_details?.state +
                              " " +
                              selectedJob?.signer_details?.zip || "N/A"}
                          </Typography>
                        </Box>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Project & Document Information */}
                <Grid size={{ xs: 12, md: 4 }}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" color="primary" gutterBottom>
                        <LocationOn sx={{ mr: 1, verticalAlign: "middle" }} />
                        Project Information
                      </Typography>
                      <Divider sx={{ mb: 2 }} />
                      <Stack spacing={2}>
                        <Box>
                          <Typography variant="subtitle2" color="textSecondary">
                            Property Address
                          </Typography>
                          <Typography variant="body1" fontWeight={500}>
                            {selectedJob.project_address ||
                              selectedJob.document?.property_address ||
                              "N/A"}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="subtitle2" color="textSecondary">
                            Owner
                          </Typography>
                          <Typography variant="body2">
                            {(
                              selectedJob?.property_owner?.name || "N/A"
                            ).toUpperCase()}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="subtitle2" color="textSecondary">
                            County
                          </Typography>
                          <Typography variant="body2">
                            {selectedJob?.county || "N/A"}
                          </Typography>
                        </Box>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              <Grid size={{ xs: 12 }}>
                {[
                  {
                    k: "Signer URL",
                    v: getCurrNotarization()?.signer_url,
                  },
                  {
                    k: "Notary URL",
                    v: getCurrNotarization()?.notary_url,
                  },
                ].map((row) => (
                  <Box key={row.k} sx={{ pl: 1.25, mt: 0.5, minWidth: 0 }}>
                    <Typography
                      variant="body2"
                      sx={{ color: "grey", fontWeight: "bold" }}
                    >
                      • {row.k}
                    </Typography>

                    {row.v ? (
                      <Tooltip
                        title="Copied"
                        placement="top"
                        open={copiedDocKey === row.k}
                        disableFocusListener
                        disableHoverListener
                        disableTouchListener
                      >
                        <Typography
                          variant="caption"
                          onClick={() => handleCopyToClipboard(row.v, row.k)}
                          sx={{
                            display: "block",
                            pl: 2,
                            mt: 0.25,
                            cursor: "pointer",
                            color: "grey",
                            maxWidth: "100%",
                            minWidth: 0,
                            whiteSpace: "normal",
                            overflowWrap: "anywhere",
                            wordBreak: "break-all", // strongest: prevents scroll on extreme strings
                            lineHeight: 1.3,
                          }}
                        >
                          {row.v}
                        </Typography>
                      </Tooltip>
                    ) : (
                      <Typography
                        variant="caption"
                        sx={{
                          pl: 2,
                          color: "grey",
                          overflowWrap: "anywhere",
                          wordBreak: "break-word",
                          minWidth: 0,
                          display: "block",
                        }}
                      >
                        N/A
                      </Typography>
                    )}
                  </Box>
                ))}
              </Grid>
            </CardContent>

            <CardActions sx={{ p: 3, pt: 0 }}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                width="100%"
              >
                <LoadingButton
                  startIcon={<Email />}
                  variant="contained"
                  color="primary"
                  loading={loading && action === "reminder"}
                  onClick={() => {
                    setAction("reminder");
                    handleSendReminder();
                  }}
                >
                  Send Reminder Email
                </LoadingButton>

                {/* <Button
                  startIcon={<Schedule />}
                  variant="outlined"
                  color="secondary"
                  onClick={() => setRescheduleDialogOpen(true)}
                >
                  Reschedule Appointment
                </Button> */}

                {/* <Button
                  startIcon={<Cancel />}
                  variant="outlined"
                  color="error"
                  onClick={() => setCancelDialogOpen(true)}
                >
                  Cancel Appointment
                </Button> */}
              </Stack>
            </CardActions>
          </Card>
        </Box>
      </Modal>

      {/* Reschedule Dialog */}
      <Dialog
        open={rescheduleDialogOpen}
        onClose={() => setRescheduleDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Reschedule Appointment</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <Alert severity="info">
              Please select a new date and time for the appointment. The
              customer will be notified automatically with the updated
              information.
            </Alert>

            <DateTimePicker
              label="New Appointment Date & Time"
              value={newDateTime}
              onChange={setNewDateTime}
              disablePast
              minutesStep={30}
              sx={{ width: "100%" }}
              shouldDisableDate={(date) => {
                const sixtyDaysFromNow = new Date();
                sixtyDaysFromNow.setDate(sixtyDaysFromNow.getDate() + 60);
                return date > sixtyDaysFromNow;
              }}
            />

            <TextField
              label="Reason for Reschedule"
              multiline
              rows={3}
              value={rescheduleReason}
              onChange={(e) => setRescheduleReason(e.target.value)}
              placeholder="Please provide a brief reason for rescheduling..."
              required
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRescheduleDialogOpen(false)}>Cancel</Button>
          <LoadingButton
            onClick={() => {
              setAction("reschedule");
              handleReschedule();
            }}
            loading={loading && action === "reschedule"}
            variant="contained"
            color="secondary"
          >
            Reschedule
          </LoadingButton>
        </DialogActions>
      </Dialog>

      {/* Cancel Dialog */}
      <Dialog
        open={cancelDialogOpen}
        onClose={() => setCancelDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Cancel Appointment</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Are you sure you want to cancel this appointment? This action cannot
            be undone.
          </Alert>
          <Typography variant="body2">
            Upon cancellation, the appointment slot will be made available for
            other notaries in your organization, and the customer will be
            notified of the cancellation.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialogOpen(false)}>
            Keep Appointment
          </Button>
          <LoadingButton
            onClick={() => {
              setAction("cancel");
              handleCancel();
            }}
            loading={loading && action === "cancel"}
            variant="contained"
            color="error"
          >
            Cancel Appointment
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default UpcomingNotarizationModal;
